<template>
    <!-- Info -->
    <text-blocks-operation-info v-if="responseData" :params="operationInfoParams" />

    <!-- Payment Info -->
    <div
    v-for="(lineGroup, index) in payInfoParser(formData, accountsBalances, selectedPayment)"
    :key="`lineGroup${index}`"
    class="step-block-wrapper">
        <divider-generic dividerSize="medium" :icon="lineGroup.icon" :title="lineGroup.label"/>
        <div v-for="line in lineGroup.lines" :key="`lineGroupLine${line,id}`">
            <!-- Text Blocks -->
            <text-block-generic
            v-if="!line.showShoppingItemsOrPaymentAlert"
            :params="line"
            :mask="line.mask"/>
            <!-- Payment Alert -->
            <infobox-generic
            v-if="line.showShoppingItemsOrPaymentAlert === 'show-payment-alert'"
            :label="t('payments.requests.baiPaga.pay.step1.amountInfoContent')"/>
            <!-- Table Shopping Cart -->
            <div v-if="line.showShoppingItemsOrPaymentAlert === 'show-items'" class="shopping-wrapper">
                <!-- Desktop Version -->
                <table-generic
                v-if="!isMobile && selectedPayment.shoppingCart"
                class="shopping-table-items"
                :headerData="tableHeaderData"
                :tableData="tableData"
                :tableStyles="tableStyles"
                :hasDetail="false"
                disabled
                noBoxStyles
                standAlone/>
                <!-- Mobile Version -->
                <shopping-cart v-else :items="selectedPayment.shoppingCart.items" :currency="selectedPayment.currency"/>
            </div>
        </div>
    </div>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import TextBlocksOperationInfo from '@/components/wrappers/OperationInfo'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import ShoppingCart from '@/components/features/payments/requests/ShoppingCart'

/* Vue */
import { computed, onMounted } from 'vue'

/* Assets & Helpers */
import { amountFormat, currencyParser } from '@/assets/scripts/amounts'
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['scrollTo'])

/** Props received
@accountsBalances { Array }
@formData { Object } - object with info for our current form
@isMobile { Boolean }
@responseData { Object } - response from payUnitelAgentRequest
@selectedPayment { Array } - Array with data received from the chosen request to pay (Payments -> Request -> Pending Payment Selected )
*/
const props = defineProps({
    accountsBalances: Array,
    formData: Object,
    isMobile: Boolean,
    responseData: Object,
    selectedPayment: Array
})

/* Operation Info */
const operationInfoParams = computed(() => {
    return {
        operationId: props.responseData.data.id,
        operationDate: props.responseData.data.mobilePaymentView.creationDate,
        errorLevel: props.responseData.errorLevel,
        code: props.responseData.code
    }
})

const payInfoParser = (formData, accountsBalances, selectedPayment) => {
    /* Merchant Info */
    const merchantBlock = {
        label: t('payments.requests.baiPaga.pay.step3.merchantDivider'),
        icon: 'cart',
        lines: [{
            label: t('payments.requests.baiPaga.pay.step3.merchantIdLabel'),
            content: selectedPayment.merchant.id
        }, {
            label: t('payments.requests.baiPaga.pay.step3.merchantNameLabel'),
            content: selectedPayment.merchant.friendlyName
        }]
    }

    /* Origin Account */
    const originAccountBlock = {
        label: t('payments.requests.baiPaga.pay.step3.originAccountDivider'),
        icon: 'account-balance',
        lines: [{
            label: t('payments.requests.baiPaga.pay.step3.originAccountLabel'),
            content: accountsBalances.find(account => account.id === formData.originAccount).accountNumber,
            mask: ACCOUNT_NUMBER
        }]
    }

    /* Shopping Cart */
    const shoppingCartBlock = {
        label: t('payments.requests.baiPaga.pay.step3.shoppingCartDivider'),
        icon: 'coin-drop',
        lines: [{
            label: t('payments.requests.baiPaga.pay.step3.descriptionLabel'),
            content: selectedPayment.description
        }]
    }

	/* Shopping Cart With Items */
     const shoppingCartWithItemsBlock = {
        label: t('payments.requests.baiPaga.pay.step3.shoppingCartDivider'),
        icon: 'coin-drop',
        lines: [{
            label: t('payments.requests.baiPaga.pay.step3.descriptionLabel'),
            content: selectedPayment.description
        },
        {
            showShoppingItemsOrPaymentAlert: 'show-items',
            items: selectedPayment.shoppingCart ? selectedPayment.shoppingCart.items : '',
            currency: selectedPayment.shoppingCart ? selectedPayment.currency : ''
        }]
    }

    /* Amount Details */
    const amountBlock = {
        label: t('payments.requests.baiPaga.pay.step3.amountDivider'),
        icon: 'money',
        lines: [{
            label: t('payments.requests.baiPaga.pay.step3.amountLabel'),
            content:
            formData.amount
            ? amountFormat(formData.amount, formData.currency)
            : t('payments.requests.baiPaga.pay.step3.amountContent',
            { estimatedAmount: amountFormat(formData.estimatedAmount, formData.currency), maxAmount: amountFormat(formData.maxAmount) })
        }]
    }

    /* Amount Details With Alert Block */
    const amountWithAlertBlock = {
        label: t('payments.requests.baiPaga.pay.step3.amountDivider'),
        icon: 'money',
        lines: [{
            label: t('payments.requests.baiPaga.pay.step3.amountLabel'),
            content:
            formData.amount
            ? amountFormat(formData.amount, formData.currency)
            : t('payments.requests.baiPaga.pay.step3.amountContent',
            { estimatedAmount: amountFormat(formData.estimatedAmount, formData.currency), maxAmount: amountFormat(formData.maxAmount) })
        }, {
            showShoppingItemsOrPaymentAlert: 'show-payment-alert'
        }]
    }

    const selectedBlocks =
    selectedPayment.shoppingCart
    ? (formData.estimatedAmount || formData.maxAmount) ? [merchantBlock, originAccountBlock, shoppingCartWithItemsBlock, amountWithAlertBlock] : [merchantBlock, originAccountBlock, shoppingCartWithItemsBlock, amountBlock]
    : (formData.estimatedAmount || formData.maxAmount) ? [merchantBlock, originAccountBlock, shoppingCartBlock, amountWithAlertBlock] : [merchantBlock, originAccountBlock, shoppingCartBlock, amountBlock]

    return selectedBlocks
}

/*************************************************
*               Shopping Cart Table              *
*************************************************/
const tableHeaderData = [
    {
        label: t('payments.requests.baiPaga.pay.step1.tableHeaderData.descriptionItemLabel'),
        dataAttr: 'description',
        showInMobile: { groupWith: 'amountPerItem' },
        rowCellType: 'title'
    },
    {
        label: t('payments.requests.baiPaga.pay.step1.tableHeaderData.amountPerItemLabel'),
        dataAttr: 'amountPerItem',
        rowCellType: 'description'
    },
    {
        label: t('payments.requests.baiPaga.pay.step1.tableHeaderData.countLabel'),
        dataAttr: 'count',
        rowCellType: 'description'
    },
    {
        label: t('payments.requests.baiPaga.pay.step1.tableHeaderData.discountLabel'),
        dataAttr: 'discount',
        rowCellType: 'description'
    }
]

const tableData = computed(() => {
    if (!props.selectedPayment.shoppingCart) return []

    const tableData = props.selectedPayment.shoppingCart.items.map(item => {
        return [
            { value: item.id, dataAttr: 'id' },
            { type: 'title', value: item.description ? item.description : '-', dataAttr: 'description' },
            { type: 'amount', value: { content: item.amountPerItem, currency: currencyParser(props.selectedPayment.currency), noBalanceIcon: true }, dataAttr: 'amountPerItem' },
            { type: 'description', value: item.count ? item.count : '-', dataAttr: 'countLabel' },
            { type: 'description', value: item.discount ? `${item.discount}%` : '-', dataAttr: 'discountLabel' }
        ]
    })

    return tableData
})

const tableStyles = [
    null,
    { 'text-align': 'end' },
    { width: '60px', 'min-width': '60px', 'text-align': 'end' },
    { width: '84px', 'min-width': '60px', 'text-align': 'end' }
]

onMounted(() => {
    emit('scrollTo', 0)
})
</script>

<style lang="scss" scoped>
.step-block-wrapper {
    display: flex;
    flex-direction: column;
    gap: 24px;

    width: 100%;
}
</style>
